import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getUser } from '@/modules/database/users'
import { getEvent } from '@/modules/database/events'

export const useFeedStore = defineStore('feed', () => {
  // State
  const users = ref<Record<string, User | null>>({})
  const events = ref<Record<string, TeamEvent | null>>({})

  // In-flight deduplication maps
  const pendingUsers = new Map<string, Promise<User | null>>()
  const pendingEvents = new Map<string, Promise<TeamEvent | null>>()

  // Getters
  const getUserById = computed(() => (userId: string): User | null => {
    return users.value[userId] ?? null
  })

  const getEventById = computed(() => (eventId: string): TeamEvent | null => {
    return events.value[eventId] ?? null
  })

  // Actions
  const fetchUser = async (userId: string): Promise<User | null> => {
    if (users.value[userId] !== undefined) {
      return users.value[userId]
    }
    const pending = pendingUsers.get(userId)
    if (pending) {
      return pending
    }

    const promise = getUser(userId)
      .then((user) => {
        users.value[userId] = user
        return user
      })
      .catch(() => {
        users.value[userId] = null
        return null
      })
      .finally(() => {
        pendingUsers.delete(userId)
      })

    pendingUsers.set(userId, promise)
    return promise
  }

  const fetchEvent = async (eventId: string): Promise<TeamEvent | null> => {
    if (events.value[eventId] !== undefined) {
      return events.value[eventId]
    }
    const pending = pendingEvents.get(eventId)
    if (pending) {
      return pending
    }

    const promise = getEvent(eventId)
      .then((event) => {
        events.value[eventId] = event
        return event
      })
      .catch(() => {
        events.value[eventId] = null
        return null
      })
      .finally(() => {
        pendingEvents.delete(eventId)
      })

    pendingEvents.set(eventId, promise)
    return promise
  }

  const clearCache = () => {
    users.value = {}
    events.value = {}
    pendingUsers.clear()
    pendingEvents.clear()
  }

  return {
    users,
    events,
    getUserById,
    getEventById,
    fetchUser,
    fetchEvent,
    clearCache
  }
})
